/**
 * Slide Viewer Styles
 */

import styled from 'styled-components';
import { motion } from 'framer-motion';
import { colors, gradients, gradientToCSS } from '@config/colors';
import { typography } from '@config/typography';
import { borderRadius } from '@config/spacing';

export const Watermark = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  pointer-events: none;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: attr(data-text);
    position: absolute;
    white-space: nowrap;
    font-family: ${typography.fontFamily.heading};
    font-size: clamp(48px, 6vw, 80px);
    font-weight: ${typography.fontWeights.semibold};
    color: rgba(51, 65, 85, 0.04);
    transform: rotate(-30deg);
    letter-spacing: 12px;
    text-transform: uppercase;
    user-select: none;
  }
`;

/* Clear, so the site's water (running beneath the router) is what the
   deck stands on; `Ground` lays the slides' light gradient over it. */
export const ViewerContainer = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  z-index: 100;
`;

/* The ground most slides stand on. It clears for a slide that stands on
   the water, and stays put while slides move over it. */
export const Ground = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${gradientToCSS(gradients.brandLight)};
  pointer-events: none;
`;

export const SlideWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MotionSlide = styled(motion.div)`
  position: absolute;
  inset: 0;
`;

export const ProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(51, 65, 85, 0.08);
  z-index: 20;
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${props => props.$progress}%;
  background: linear-gradient(90deg, ${colors.brand.exxtraDark}, ${colors.brand.primaryDark});
  transition: width 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
`;

export const NavButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$side === 'left' ? 'left: 16px;' : 'right: 16px;'}
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(51, 65, 85, 0.1);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  color: ${colors.highlight.light};
  font-size: ${typography.fontSize.lg};
  cursor: pointer;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease, background 0.2s ease, color 0.2s ease;

  ${ViewerContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    color: ${colors.highlight.dark};
    border-color: rgba(51, 65, 85, 0.2);
  }

  &:disabled {
    opacity: 0 !important;
    cursor: default;
  }
`;

export const SlideCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.xs};
  color: ${colors.highlight.light};
  z-index: 20;
  letter-spacing: ${typography.letterSpacing.wide};
`;

export const MenuButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.md};
  border: 1px solid rgba(51, 65, 85, 0.1);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  color: ${colors.highlight.light};
  font-size: ${typography.fontSize.lg};
  cursor: pointer;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease, background 0.2s ease;

  ${ViewerContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    color: ${colors.highlight.dark};
  }
`;

export const DeckTitle = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xs};
  color: ${colors.highlight.light};
  z-index: 20;
  letter-spacing: ${typography.letterSpacing.wide};
  text-transform: uppercase;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${ViewerContainer}:hover & {
    opacity: 1;
  }
`;

export const Copyright = styled.div`
  position: absolute;
  bottom: 18px;
  right: 0%;
  transform: translateX(-50%);
  font-family: ${typography.fontFamily.heading};
  font-size: 10px;
  color: ${colors.highlight.extraLight};
  z-index: 20;
  letter-spacing: 0.5px;
  white-space: nowrap;
  user-select: none;
`;
